import { useState, useEffect, useRef, useMemo } from 'react';
import { CheckCircle, AlertTriangle, Star, GripVertical, Info, Plus } from 'lucide-react';
import { useCalendarPreferences } from '../../hooks/useCalendarPreferences';
import { useGoogleCalendar } from '../../hooks/useGoogleCalendar';
import { useGoogleAccounts } from '../../hooks/useGoogleAccounts';
import { useSupporter } from '../../hooks/useSupporter';
import { useTranslation } from 'react-i18next';

const CalendarPreferences = ({ isOpen, onClose, onCalendarChange }) => {
  const { t } = useTranslation();
  const { 
    calendars, 
    loading, 
    error, 
    toggleCalendar, 
    updateCalendarColor,
    fetchCalendars,
    saveAllCalendarPreferences,
    reorderCalendars
  } = useCalendarPreferences();
  
  const { refreshStatus } = useGoogleCalendar();

  const { isSupporter } = useSupporter();
  const { addAccount, accounts, disconnectAccount } = useGoogleAccounts();

  // Sort calendars: enabled first, then disabled. Within each group, by
  // displayOrder. This matches the visible order in the day-card calendar
  // rows (which only show enabled calendars, ordered by displayOrder).
  const orderedCalendars = useMemo(() => {
    const arr = [...(calendars || [])];
    arr.sort((a, b) => {
      if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
      const ad = a.displayOrder ?? 0;
      const bd = b.displayOrder ?? 0;
      if (ad !== bd) return ad - bd;
      return (a.name || '').localeCompare(b.name || '');
    });
    return arr;
  }, [calendars]);

  const [isAddingAccount, setIsAddingAccount] = useState(false);
  const [disconnectingAccountId, setDisconnectingAccountId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [draggedCalendar, setDraggedCalendar] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [openInfoId, setOpenInfoId] = useState(null);
  const infoPopoverRef = useRef(null);

  const handleToggleCalendar = (calendarId, tokenId, enabled) => {
    toggleCalendar(calendarId, tokenId, enabled);
    setHasChanges(true);
    setJustSaved(false);
  };

  const handleColorChange = (calendarId, tokenId, color) => {
    updateCalendarColor(calendarId, tokenId, color);
    setHasChanges(true);
    setJustSaved(false);
  };

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);
      await saveAllCalendarPreferences();
      setHasChanges(false);
      // Full reload so calendar add/remove/reorder changes take effect
      // everywhere (event cache, day-card rows, group state) without
      // having to thread invalidation through every consumer.
      window.location.reload();
    } catch (error) {
      console.error('Failed to save calendar preferences:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRefresh = () => {
    fetchCalendars().catch(error => {
      console.error('Error in handleRefresh (handled):', error);
      // Error is already handled in fetchCalendars hook
    });
  };

  const handleDisconnectAccount = async (tokenId, email) => {
    if (!window.confirm(t('account.confirmDisconnect', { email }))) {
      return;
    }
    try {
      setDisconnectingAccountId(tokenId);
      await disconnectAccount(tokenId);
      await fetchCalendars();
      await refreshStatus();
      if (onCalendarChange) onCalendarChange();
    } catch (err) {
      console.error('Failed to disconnect Google account:', err);
    } finally {
      setDisconnectingAccountId(null);
    }
  };

  const handleAddAccount = async () => {
    try {
      setIsAddingAccount(true);
      await addAccount();
      // Refresh calendars to show calendars from the new account
      await fetchCalendars();
      await refreshStatus();
      if (onCalendarChange) {
        onCalendarChange();
      }
    } catch (err) {
      console.error('Failed to add Google account:', err);
    } finally {
      setIsAddingAccount(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, calendar, index) => {
    setDraggedCalendar({ calendar, originalIndex: index });
    try {
      e.dataTransfer.setData('text/plain', calendar.id);
    } catch (_) {}
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (!draggedCalendar) return;

    const draggedIndex = draggedCalendar.originalIndex;
    if (draggedIndex === dropIndex) return;

    // Reorder using the same array we render (orderedCalendars), so
    // dragIndex/dropIndex match the visible position. The hook then
    // recomputes displayOrder from the new array's order.
    const reorderedCalendars = [...orderedCalendars];
    const [movedCalendar] = reorderedCalendars.splice(draggedIndex, 1);
    reorderedCalendars.splice(dropIndex, 0, movedCalendar);

    reorderCalendars(reorderedCalendars);
    setHasChanges(true);
    setJustSaved(false);
    setDraggedCalendar(null);
  };

  const handleDragEnd = () => {
    setDraggedCalendar(null);
    setDragOverIndex(null);
  };

  // Close info popover when clicking outside
  useEffect(() => {
    const handleDocMouseDown = (e) => {
      if (openInfoId && infoPopoverRef.current && !infoPopoverRef.current.contains(e.target)) {
        setOpenInfoId(null);
      }
    };
    document.addEventListener('mousedown', handleDocMouseDown);
    return () => document.removeEventListener('mousedown', handleDocMouseDown);
  }, [openInfoId]);

  // Fetch calendars when modal is opened
  useEffect(() => {
    if (isOpen) {
      fetchCalendars().catch(error => {
        console.error('Error in CalendarPreferences fetchCalendars (handled):', error);
        // Error is already handled in fetchCalendars hook
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Auto-close modal if calendar gets disconnected
  useEffect(() => {
    if (isOpen && error === 'google_calendar_not_connected') {
      // Close this modal after a short delay to show the message briefly
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, error, onClose]);

  if (!isOpen) return null;

  return (
    <div className="calendar-preferences-overlay">
      <div
        className="calendar-preferences-modal"
        style={{
          width: '560px',
          maxWidth: '90vw',
        }}
      >
        <div className="modal-header">
          <h3>{t('calendar.settings')}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        {/* Add Account button */}
        <div className="cp-section cp-section--accounts">
          <span className="cp-account-count">
            {accounts.length} {accounts.length === 1 ? t('account.singular') : t('account.plural')} {t('account.connectedSuffix')}
          </span>
          <button
            onClick={handleAddAccount}
            disabled={isAddingAccount}
            className="cp-btn cp-btn--ghost"
          >
            <Plus size={13} />
            {isAddingAccount ? t('account.adding') : t('account.addAccount')}
          </button>
        </div>

        {/* Connected accounts — listed straight from our DB so accounts whose
            Google access was revoked still appear here and can be disconnected. */}
        {accounts.length > 0 && (
          <div className="cp-account-list">
            {accounts.map((account) => {
              const isDisc = disconnectingAccountId === account.id;
              return (
                <div key={account.id} className="cp-account-row">
                  <span className="cp-account-email">{account.email}</span>
                  <button
                    onClick={() => handleDisconnectAccount(account.id, account.email)}
                    disabled={isDisc}
                    className="cp-btn cp-btn--danger-link"
                  >
                    {isDisc ? t('calendar.disconnecting') : t('calendar.disconnect')}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Supporter upgrade message for non-supporters */}
        {!isSupporter && (
          <div className="upgrade-message">
            <div style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '16px',
              borderRadius: '8px',
              color: 'white',
              margin: '16px 0',
              textAlign: 'center'
            }}>
              <Star size={20} style={{ marginBottom: '8px' }} />
              <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                {t('upgrade.calendarTitle')}
              </h4>
              <p style={{ margin: '0 0 12px 0', fontSize: '14px', opacity: '0.9' }}>
                {t('upgrade.calendarBody')}
              </p>
              <button 
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onClick={() => {
                  // Could link to upgrade page or show upgrade modal
                  console.log('Upgrade clicked');
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                {t('upgrade.becomeSupporter')}
              </button>
            </div>
          </div>
        )}
        
        <div className="modal-content">
          {loading && (
            <div className="loading-state">
              <p>{t('calendar.loadingCalendars')}</p>
            </div>
          )}
          
          {error && error !== 'google_calendar_not_connected' && (
            <div className="error-state">
              <p><AlertTriangle size={16} className="inline mr-1" /> {error}</p>
              <button onClick={handleRefresh} className="retry-button">
                {t('buttons.retry')}
              </button>
            </div>
          )}
          
          {error === 'google_calendar_not_connected' && (
            <div className="not-connected-state" style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#6b7280',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              margin: '16px 0'
            }}>
              <p style={{ marginBottom: '16px', fontWeight: '500' }}>
                {t('calendar.notConnected')}
              </p>
              <p style={{ marginBottom: '20px', fontSize: '14px' }}>
                {t('calendar.notConnectedBody')}
              </p>
              <button 
                onClick={onClose}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {t('buttons.closeNow')}
              </button>
            </div>
          )}
          
          {!loading && !error && calendars.length === 0 && (
            <div className="empty-state">
              <p>{t('calendar.noCalendarsFound')}</p>
              <button onClick={handleRefresh} className="refresh-button">
                {t('buttons.refresh')}
              </button>
            </div>
          )}
          
          {!loading && orderedCalendars.length > 0 && (
            <div className="calendars-list" style={{ marginTop: '6px' }}>
              {orderedCalendars.map((calendar, index) => {
                  const isDragging = draggedCalendar?.calendar.id === calendar.id && draggedCalendar?.calendar.tokenId === calendar.tokenId;
                  const showDropTop = dragOverIndex === index;
                  return (
                    <div
                      key={`${calendar.id}-${calendar.tokenId || index}`}
                      className={`calendar-item ${showDropTop ? 'drag-over' : ''}${calendar.enabled ? ' calendar-item--enabled' : ''}`}
                      draggable={true}
                      onDragStart={(e) => handleDragStart(e, calendar, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnter={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      style={{
                        cursor: 'grab',
                        opacity: isDragging ? 0.6 : 1,
                        borderTop: showDropTop ? '2px solid var(--wv-accent, #3b82f6)' : 'none',
                        transition: 'background-color 0.15s ease, opacity 0.15s ease',
                        padding: '4px 8px',
                        backgroundColor: calendar.enabled ? 'var(--wv-accent-soft, #f8fafc)' : 'transparent',
                        borderRadius: '4px',
                        marginBottom: '2px',
                        userSelect: 'none',
                      }}
                    >
                      <div
                        className="calendar-row"
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '14px 16px 1fr auto',
                          columnGap: '6px',
                          alignItems: 'center',
                          minHeight: '24px',
                          position: 'relative',
                        }}
                      >
                        {/* Drag handle */}
                        <div
                          style={{
                            color: 'var(--wv-text-faint, #94a3b8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          aria-hidden
                          draggable={true}
                          onDragStart={(e) => handleDragStart(e, calendar, index)}
                        >
                          <GripVertical size={13} />
                        </div>

                        {/* Toggle */}
                        <div>
                          <input
                            type="checkbox"
                            id={`calendar-${calendar.id}-${calendar.tokenId}`}
                            checked={calendar.enabled}
                            onChange={(e) => handleToggleCalendar(calendar.id, calendar.tokenId, e.target.checked)}
                            disabled={isSaving}
                            style={{ width: 14, height: 14 }}
                          />
                        </div>

                        {/* Name and description (info) */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                          <span id={`calendar-name-${calendar.id}`} className="calendar-name" style={{ fontSize: 13, fontWeight: 500, color: 'var(--wv-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {calendar.name}
                            {calendar.accountEmail && (
                              <span style={{ fontWeight: 400, color: 'var(--wv-text-muted, #6b7280)', marginLeft: 4 }}>
                                ({calendar.accountEmail})
                              </span>
                            )}
                          </span>
                          {calendar.description && (
                            <span
                              style={{ color: '#64748b', display: 'inline-flex', alignItems: 'center', position: 'relative' }}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenInfoId((prev) => (prev === calendar.id ? null : calendar.id));
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setOpenInfoId((prev) => (prev === calendar.id ? null : calendar.id));
                                }
                              }}
                              aria-label={t('calendar.showDescription')}
                            >
                              <Info size={14} />
                              {openInfoId === calendar.id && (
                                <div
                                  ref={infoPopoverRef}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  style={{
                                    position: 'absolute',
                                    top: 20,
                                    left: -4,
                                    zIndex: 30,
                                    background: '#111827',
                                    color: '#fff',
                                    padding: '8px 10px',
                                    borderRadius: 8,
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                                    maxWidth: 360,
                                    fontSize: 12,
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {calendar.description}
                                </div>
                              )}
                            </span>
                          )}
                        </div>

                        {/* Color (aligned right) */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', justifySelf: 'end', gap: 8 }}>
                          {calendar.enabled ? (
                            <input
                              type="color"
                              id={`color-${calendar.id}-${calendar.tokenId}`}
                              value={calendar.color}
                              onChange={(e) => handleColorChange(calendar.id, calendar.tokenId, e.target.value)}
                              disabled={isSaving}
                              style={{ width: 28, height: 20, padding: 0, border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff' }}
                            />
                          ) : (
                            // placeholder to keep column width consistent
                            <div style={{ width: 28, height: 20 }} />
                          )}
                          {calendar.primary && (
                            <span className="primary-badge" style={{
                              fontSize: 11,
                              backgroundColor: '#e0e7ff',
                              color: '#4338ca',
                              padding: '2px 6px',
                              borderRadius: 9999,
                              lineHeight: 1,
                              alignSelf: 'center'
                            }}>{t('calendar.primary')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
          
          
        </div>
        
        <div className="modal-footer">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {/* Left side: Save button or Saved pill */}
            {!loading && !error && calendars.length > 0 && (
              hasChanges ? (
                <button 
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    height: 36
                  }}
                >
                  {isSaving ? t('buttons.saving') : t('buttons.saveChanges')}
                </button>
              ) : (
                justSaved && (
                  <div
                    className="saved-indicator"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 14px',
                      borderRadius: 9999,
                      backgroundColor: '#10b981',
                      color: 'white',
                      fontSize: 14,
                      fontWeight: 600,
                      boxShadow: '0 6px 16px rgba(16,185,129,0.25)',
                      height: 36
                    }}
                    aria-live="polite"
                  >
                    <CheckCircle size={16} />
                    <span>{t('buttons.saved')}</span>
                  </div>
                )
              )
            )}
            
            {/* Right side: Done */}
            <button onClick={onClose} className="close-modal-button">
              {t('buttons.done')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarPreferences;